// Chemin: C:\gestion-copro\src\pages\vigie\NousContacterVigiePage.tsx
// Session 151 : Page "Nous contacter" — remplace le BoutonAideFlottant
// Session 186 : Renommée "Avis & idées" — label plus engageant
// Deux sections : Suggérer une amélioration + Donner son avis
// Les données vont dans les mêmes collections Firestore (feedbackSuggestions, feedbackAvis)

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Lightbulb,
  Star,
  Send,
  CheckCircle,
  Loader2,
  HelpCircle,
  MessageSquare,
  ChevronLeft,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const NousContacterVigiePage: React.FC = () => {
  const { userProfile } = useAuth();

  // --- États suggestion ---
  const [suggestionTexte, setSuggestionTexte] = useState('');
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionEnvoyee, setSuggestionEnvoyee] = useState(false);

  // --- États avis ---
  const [avisNote, setAvisNote] = useState(0);
  const [avisHoverNote, setAvisHoverNote] = useState(0);
  const [avisCommentaire, setAvisCommentaire] = useState('');
  const [avisLoading, setAvisLoading] = useState(false);
  const [avisEnvoye, setAvisEnvoye] = useState(false);

  // ============================================
  // ENVOI SUGGESTION
  // ============================================

  const handleEnvoyerSuggestion = async () => {
    if (!suggestionTexte.trim() || !userProfile) return;
    setSuggestionLoading(true);
    try {
      await addDoc(collection(db, 'feedbackSuggestions'), {
        userId: userProfile.id || '',
        userEmail: userProfile.email || '',
        userPrenom: userProfile.prenom || '',
        userNom: userProfile.nom || '',
        texte: suggestionTexte.trim(),
        statut: 'nouveau',
        createdAt: serverTimestamp(),
      });
      setSuggestionEnvoyee(true);
      setSuggestionTexte('');
    } catch (error) {
      console.error('Erreur envoi suggestion:', error);
      alert('Une erreur est survenue. Réessayez.');
    } finally {
      setSuggestionLoading(false);
    }
  };

  // ============================================
  // ENVOI AVIS
  // ============================================

  const handleEnvoyerAvis = async () => {
    if (avisNote === 0 || !userProfile) return;
    setAvisLoading(true);
    try {
      await addDoc(collection(db, 'feedbackAvis'), {
        userId: userProfile.id || '',
        userEmail: userProfile.email || '',
        userPrenom: userProfile.prenom || '',
        userNom: userProfile.nom || '',
        nomCopropriete: userProfile.vigieInfo?.nomCopropriete || '',
        note: avisNote,
        commentaire: avisCommentaire.trim(),
        statut: 'nouveau',
        publie: false,
        createdAt: serverTimestamp(),
      });
      setAvisEnvoye(true);
      setAvisNote(0);
      setAvisCommentaire('');
    } catch (error) {
      console.error('Erreur envoi avis:', error);
      alert('Une erreur est survenue. Réessayez.');
    } finally {
      setAvisLoading(false);
    }
  };

  // ============================================
  // LABELS NOTE
  // ============================================

  const getNoteLabel = (n: number): string => {
    switch (n) {
      case 1: return 'Décevant';
      case 2: return 'Peut mieux faire';
      case 3: return 'Correct';
      case 4: return 'Bien';
      case 5: return 'Excellent !';
      default: return '';
    }
  };

  // ============================================
  // RENDU
  // ============================================

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-xl">
            <MessageSquare className="w-6 h-6 text-blue-600" />
          </div>
          Avis & idées
        </h1>
        <p className="text-gray-500 mt-2">
          Vos retours nous aident à améliorer Copro Protect. Chaque idée compte !
        </p>
      </div>

      {/* Lien vers la FAQ */}
      <Link
        to="/vigie/faq"
        className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors group"
      >
        <div className="p-2 bg-blue-200 rounded-lg group-hover:bg-blue-300 transition-colors">
          <HelpCircle className="w-5 h-5 text-blue-700" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-blue-900">Vous avez une question ?</p>
          <p className="text-xs text-blue-600">Consultez d'abord notre FAQ — la réponse s'y trouve peut-être.</p>
        </div>
        <ChevronLeft className="w-4 h-4 text-blue-400 rotate-180" />
      </Link>

      {/* --- SECTION SUGGESTION --- */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-200 rounded-xl">
              <Lightbulb className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Suggérer une amélioration</h2>
              <p className="text-xs text-gray-500">Une idée, une fonctionnalité manquante ? Dites-le nous.</p>
            </div>
          </div>
        </div>

        <div className="p-5">
          {suggestionEnvoyee ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <p className="font-semibold text-gray-900 mb-1">Merci pour votre suggestion !</p>
              <p className="text-sm text-gray-500 mb-4">Nous la lirons avec attention.</p>
              <button
                onClick={() => setSuggestionEnvoyee(false)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Envoyer une autre suggestion
              </button>
            </div>
          ) : (
            <>
              <textarea
                value={suggestionTexte}
                onChange={(e) => setSuggestionTexte(e.target.value)}
                placeholder="Décrivez votre suggestion ou l'amélioration souhaitée..."
                className="w-full h-32 p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-xs text-gray-400">
                  {suggestionTexte.trim().length > 0 ? `${suggestionTexte.trim().length} caractères` : 'Minimum quelques mots'}
                </p>
                <button
                  onClick={handleEnvoyerSuggestion}
                  disabled={!suggestionTexte.trim() || suggestionLoading}
                  className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {suggestionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Envoyer
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* --- SECTION AVIS --- */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-200 rounded-xl">
              <Star className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Donner mon avis</h2>
              <p className="text-xs text-gray-500">30 secondes pour nous aider à progresser.</p>
            </div>
          </div>
        </div>

        <div className="p-5">
          {avisEnvoye ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <p className="font-semibold text-gray-900 mb-1">Merci pour votre avis !</p>
              <p className="text-sm text-gray-500 mb-4">Il nous aide à améliorer Copro Protect.</p>
              <button
                onClick={() => setAvisEnvoye(false)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Donner un nouvel avis
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Note étoiles */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Comment notez-vous Copro Protect ?</p>
                <div className="flex items-center gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setAvisNote(n)}
                      onMouseEnter={() => setAvisHoverNote(n)}
                      onMouseLeave={() => setAvisHoverNote(0)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-9 h-9 transition-colors ${
                          n <= (avisHoverNote || avisNote)
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {avisNote > 0 && (
                  <p className="text-center text-sm text-gray-500 mt-2">{getNoteLabel(avisNote)}</p>
                )}
              </div>

              {/* Commentaire */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Un commentaire ? <span className="text-gray-400 font-normal">(facultatif)</span>
                </p>
                <textarea
                  value={avisCommentaire}
                  onChange={(e) => setAvisCommentaire(e.target.value)}
                  placeholder="Ce que vous appréciez, ce qui pourrait être amélioré..."
                  className="w-full h-24 p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>

              {/* Info publication */}
              <p className="text-xs text-gray-400 text-center">
                Votre avis pourra être publié de façon anonymisée sur notre site, avec votre prénom uniquement.
              </p>

              {/* Bouton envoi */}
              <div className="flex justify-end">
                <button
                  onClick={handleEnvoyerAvis}
                  disabled={avisNote === 0 || avisLoading}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {avisLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Envoyer mon avis
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NousContacterVigiePage;
